import {
  AuthError,
  BrowserAuthError,
  BrowserAuthErrorCodes,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';

// BrowserAuthError codes from acquireTokenSilent that mean "the hidden-iframe
// renewal could not complete" (expired Entra session cookie, third-party
// cookies blocked). An interactive redirect fixes these, so the browser flow
// treats them like InteractionRequiredAuthError. Deliberately NOT here:
//   - no_network_connectivity: network errors must surface.
//   - block_iframe_reload: thrown when the app itself runs inside MSAL's hidden
//     iframe; interactive sign-in cannot fix that.
const SILENT_RENEWAL_FAILURE_CODES: ReadonlySet<string> = new Set([
  BrowserAuthErrorCodes.timedOut, // "timed_out" (iframe bridge timeout)
  BrowserAuthErrorCodes.redirectBridgeEmptyResponse, // "redirect_bridge_empty_response"
  // Pre-v3 MSAL name for timed_out; not exported by msal-browser 5, kept as
  // a literal so the classification stays correct if it ever resurfaces.
  'monitor_window_timeout',
]);

export function shouldFallBackToInteractive(err: unknown): boolean {
  if (err instanceof InteractionRequiredAuthError) return true;
  if (err instanceof BrowserAuthError) {
    return SILENT_RENEWAL_FAILURE_CODES.has(err.errorCode);
  }
  return false;
}

export function authErrorCode(err: unknown): string | undefined {
  return err instanceof AuthError ? err.errorCode : undefined;
}

// Redirect-loop guard.
//
// acquireTokenRedirect navigates away, so the "already tried" marker has to
// survive the round trip: it lives in sessionStorage (value = Date.now()), set
// just before the redirect and cleared on the next successful token.
//
//   - While a redirect is in flight in this page load, concurrent callers
//     (Dashboard mounts useBookmarks + useDashboardData together) never start
//     a second one and never touch the marker; they wait for the navigation.
//   - On the page load after the round trip, a fresh marker (< 5 min) means
//     the redirect did not help: the error is surfaced, and the marker is
//     consumed into a page-load flag so every call in this load surfaces it.
//     A manual reload afterwards gets one fresh redirect attempt.
//   - Markers older than 5 minutes are ignored so a stale one cannot block a
//     later legitimate redirect.
//   - If sessionStorage is unusable the guard fails safe: no redirect at all,
//     because without the marker a cross-page-load loop could not be stopped.
export const REDIRECT_ATTEMPT_KEY = 'whatsnew.auth.tokenRedirectAttempted';
export const REDIRECT_MARKER_MAX_AGE_MS = 5 * 60 * 1000;

let redirectInFlight: Promise<void> | null = null;
let redirectBlockedThisLoad = false;

type MarkerState = 'fresh' | 'none' | 'unavailable';

function readMarker(): MarkerState {
  try {
    const raw = sessionStorage.getItem(REDIRECT_ATTEMPT_KEY);
    if (raw === null) return 'none';
    const age = Date.now() - Number(raw);
    if (Number.isFinite(age) && age >= 0 && age < REDIRECT_MARKER_MAX_AGE_MS) return 'fresh';
    sessionStorage.removeItem(REDIRECT_ATTEMPT_KEY); // stale or garbage
    return 'none';
  } catch {
    return 'unavailable';
  }
}

function writeMarker(): boolean {
  try {
    sessionStorage.setItem(REDIRECT_ATTEMPT_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

function clearMarker(): void {
  try {
    sessionStorage.removeItem(REDIRECT_ATTEMPT_KEY);
  } catch {}
}

// The redirect started by another caller in this page load, if any.
export function pendingRedirect(): Promise<void> | null {
  return redirectInFlight;
}

// True when a new redirect must NOT be started automatically.
export function redirectAlreadyAttempted(): boolean {
  // In flight: answer without reading or clearing the marker, which has to
  // survive the navigation.
  if (redirectInFlight || redirectBlockedThisLoad) return true;
  const marker = readMarker();
  if (marker === 'none') return false;
  redirectBlockedThisLoad = true;
  if (marker === 'fresh') clearMarker();
  return true;
}

// Writes the marker, then runs `navigate`. Returns null (and does not
// navigate) if the marker cannot be written; the caller surfaces the error.
export function startGuardedRedirect(navigate: () => Promise<void>): Promise<void> | null {
  if (!writeMarker()) {
    redirectBlockedThisLoad = true;
    return null;
  }
  redirectInFlight = navigate();
  return redirectInFlight;
}

// Call on every successful token.
export function clearRedirectAttempt(): void {
  redirectInFlight = null;
  redirectBlockedThisLoad = false;
  clearMarker();
}
